import React from 'react';
import { Typography, Checkbox, Box } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import DynamicTable, { DynamicColumn } from '../ui/DynamicTable';

interface CustomField {
  id: number;
  field: {
    name: string;
    value: string;
    valueDate?: string;
  };
  comment?: string;
  status?: 'synced' | 'pending';
}

interface CustomFieldsInCDPProps {
  customFields?: CustomField[];
  selectedItems: Set<string>;
  onCheckboxChange: (itemId: string, itemType: string) => void;
  showSyncColumn?: boolean;
  fieldConfigs?: any;
  headerText?: string;
}

const CustomFieldsInCDPSection: React.FC<CustomFieldsInCDPProps> = ({
  customFields,
  selectedItems,
  onCheckboxChange,
  showSyncColumn = true,
  headerText,
}) => {
  const [expanded, setExpanded] = React.useState(true);

  if (!customFields || customFields.length === 0) return null;

  const columns: DynamicColumn<CustomField>[] = [
    {
      id: 'fieldName',
      label: 'Field Name',
      accessor: 'field',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.field.name}</Typography>,
    },
    {
      id: 'fieldValue',
      label: 'Field Value',
      accessor: 'field',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.field.value}</Typography>,
    },
    {
      id: 'date',
      label: 'Date',
      accessor: 'field',
      render: (row) => (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          {row.field.valueDate || '-'}
        </Typography>
      ),
    },
    {
      id: 'comment',
      label: 'Comment',
      accessor: 'comment',
      render: (row) => <Typography sx={{ fontSize: '12px' }}>{row.comment || '-'}</Typography>,
    },
    ...(showSyncColumn
      ? [
          {
            id: 'sync',
            label: 'Sync',
            accessor: 'status' as keyof CustomField,
            render: (row: CustomField) => (
              <Box sx={{ textAlign: 'center' }}>
                {row.status === 'synced' ? (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '11px', color: '#4caf50', fontWeight: 500 }}
                  >
                    Synced
                  </Typography>
                ) : (
                  <Checkbox
                    checked={selectedItems.has(`customField-${row.id}`)}
                    onChange={() => onCheckboxChange(row.id.toString(), 'customField')}
                    size="small"
                    sx={{
                      color: '#255E5E',
                      '&.Mui-checked': {
                        color: '#255E5E',
                      },
                    }}
                  />
                )}
              </Box>
            ),
          },
        ]
      : []),
  ];

  return (
    <CustomAccordion
      title={headerText || `Custom Fields in CDP (${customFields.length})`}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mt: 1.5,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
          backgroundColor: '#255E5E',
          color: 'white',
          borderRadius: '4px 4px 0 0',
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
          color: 'white !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
        '&:before': { display: 'none' },
        '& .MuiAccordionDetails-root': {
          p: 0,
          border: '2px solid #255E5E',
          borderTop: 'none',
        },
      }}
      summarySx={{
        backgroundColor: '#255E5E',
        color: 'white',
        '& .MuiTypography-root': {
          color: 'white',
          fontSize: '15px',
          fontWeight: 400,
        },
        '& .MuiSvgIcon-root': {
          color: 'white',
        },
      }}
      detailsSx={{
        p: 0,
        border: '2px solid #255E5E',
        borderTop: 'none',
      }}
    >
      <DynamicTable
        columns={columns}
        data={customFields}
        variant="compact"
        noDataMessage="No custom fields found"
      />
    </CustomAccordion>
  );
};

export default CustomFieldsInCDPSection;
